import { TransferFundsFlow } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge/resources/bridgeApi/types';
import axios from 'axios';
import { callApi } from 'services/config';
import { generateHmacSignature, generateUuidWithoutDashes } from 'utils/crypto';

// Interface for transaction history response
interface TransactionHistoryResponse {
  count: number;
  data: Array<{
    source: {
      description: string;
      payment_rail: string;
      sender_routing_number: string;
      sender_name: string;
      wire_message: string;
    };
    id: string;
    customer_id: string;
    virtual_account_id: string;
    type: string;
    amount: string;
    currency: string;
    developer_fee_amount: string;
    exchange_fee_amount: string;
    created_at: string;
    subtotal_amount: string;
    gas_fee: string;
    deposit_id: string;
    destination_tx_hash: string;
  }>;
}

export const getBridgeKYCLink = async ({ userId }: { userId: string }) => {
  const url = `/bridge/customers/${userId}/kyc-link`;
  return await callApi(url, 'GET', {});
};

/**
 * Get transaction history for virtual accounts
 * @param userId - The user ID to fetch transaction history for
 * @returns Promise containing transaction history data
 */
export const getVirtualAccountsHistory = async ({
  userId,
}: {
  userId: string;
}): Promise<TransactionHistoryResponse> => {
  const url = `/bridge/users/${userId}/virtual-accounts-history`;
  return await callApi(url, 'GET', {});
};

/**
 * Get preview amount for a transfer
 * @param fromWalletId - The ID of the wallet to transfer from
 * @param toWalletId - The ID of the wallet to transfer to
 * @param amount - The amount to transfer
 * @param currency - The currency of the amount
 * @param flow - The flow of the transfer
 * @returns Promise containing preview amount data
 */
export const getPreviewAmount = async ({
  fromWalletId,
  toWalletId,
  amount,
  currency,
  flow,
}: {
  fromWalletId: string;
  toWalletId: string;
  amount: string;
  currency: string;
  flow: TransferFundsFlow;
}) => {
  const url = `/bridge/wallets/transfer`;
  const data = {
    fromWalletId,
    toWalletId,
    amount,
    currency,
    flow,
    preview: true,
  };
  return await callApi(url, 'POST', data);
};

// Interface for ephemeral key response
interface EphemeralKeyResponse {
  ephemeral_key: string;
}

// Interface for Bridge card details response
export interface BridgeCardDetailsResponse {
  card_number: string;
  expiry_date: string;
  card_security_code: string;
}

/**
 * Fetches card details directly from Bridge API using ephemeral key (PCI-compliant)
 * @param ephemeralKey - The ephemeral key obtained from createEphemeralKey
 * @param clientSecret - The client secret used to generate the nonce
 * @param clientTimestamp - The timestamp in seconds used to generate the nonce
 * @returns Promise containing card details
 */
export const getCardDetailsFromBridge = async ({
  ephemeralKey,
  clientSecret,
  clientTimestamp,
}: {
  ephemeralKey: string;
  clientSecret: string;
  clientTimestamp: number;
}): Promise<BridgeCardDetailsResponse> => {
  const url = `https://cards-pci.bridge.xyz/v0/card_details/?secret=${clientSecret}&timestamp=${clientTimestamp}`;

  const response = await axios.get<BridgeCardDetailsResponse>(url, {
    headers: {
      Authorization: `Bearer ${ephemeralKey}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

/**
 * Fetches card details using the PCI-compliant ephemeral key approach
 * This is the main function to use for fetching sensitive card details
 * @param theraFernApi - The Thera Fern API client
 * @param customerId - The Bridge customer ID
 * @param cardAccountId - The card account ID
 * @returns Promise containing card details
 */
export const fetchCardDetailsPCICompliant = async ({
  theraFernApi,
  customerId,
  cardAccountId,
}: {
  theraFernApi: any;
  customerId: string;
  cardAccountId: string;
}): Promise<BridgeCardDetailsResponse> => {
  // Step 1: Generate client secret (UUID without dashes)
  const clientSecret = generateUuidWithoutDashes();

  // Step 2: Generate client timestamp (current time in seconds)
  const clientTimestamp = Math.floor(Date.now() / 1000);

  // Step 3: Generate client nonce using HMAC-SHA256
  const clientNonce = await generateHmacSignature(clientSecret, clientTimestamp);

  // Step 4: Create ephemeral key request
  const ephemeralKeyRequest = {
    clientNonce: clientNonce,
  };

  // Step 5: Get ephemeral key from backend
  const ephemeralKeyResponse: EphemeralKeyResponse = await theraFernApi.thera.bridge.bridgeApi.createEphemeralKey(
    customerId,
    cardAccountId,
    ephemeralKeyRequest,
  );

  // Step 6: Fetch card details directly from Bridge using the ephemeral key
  const cardDetails = await getCardDetailsFromBridge({
    ephemeralKey: ephemeralKeyResponse.ephemeral_key,
    clientSecret,
    clientTimestamp,
  });

  return cardDetails;
};
